import { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { GLASS } from '../home/glass';

export const DOSSIER_IMAGES = {
    glycine: '/ingredients/glycine.webp',
    magnesium: '/ingredients/magnesium.webp',
    ltheanine: '/ingredients/theanine.webp',
    lemonbalm: '/ingredients/lemonbalm.webp',
    inulin: '/ingredients/chicory.webp',
};

const BAND = { rootMargin: '-50% 0px -50% 0px', threshold: 0 };

// Mobile-only: 5 glass dots, shown only while the ingredient dossiers are in view.
export function DossierDock({ dossiers, containerRef }) {
    const { lang } = useLanguage();
    const [visible, setVisible] = useState(false);
    const [active, setActive] = useState(dossiers[0]?.id);

    useEffect(() => {
        const box = containerRef.current;
        if (!box) return undefined;
        // Visible from the first dossier through the end of the sources list.
        const update = () => {
            const mid = window.innerHeight / 2;
            const end = document.getElementById('sources') || box;
            setVisible(box.getBoundingClientRect().top <= mid && end.getBoundingClientRect().bottom >= mid);
        };
        update();
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        const act = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
            BAND
        );
        dossiers.forEach((d) => {
            const el = document.getElementById(d.id);
            if (el) act.observe(el);
        });
        return () => {
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            act.disconnect();
        };
    }, [dossiers, containerRef]);

    const jump = (id) => {
        const el = document.getElementById(id);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: 'smooth' });
    };

    return (
        <nav
            aria-label={lang === 'nl' ? 'Ingrediënten' : 'Ingredients'}
            className={`lg:hidden fixed inset-x-0 bottom-4 z-40 flex justify-center pointer-events-none transition-[opacity,transform] duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            data-testid="science-dossier-dock"
            data-visible={visible}
            aria-hidden={!visible}
        >
            <div className={`flex gap-2.5 p-2 !rounded-full ${GLASS} !bg-white/55 ${visible ? 'pointer-events-auto' : ''}`}>
                {dossiers.map((d) => (
                    <button
                        key={d.id}
                        type="button"
                        onClick={() => jump(d.id)}
                        aria-label={d.name[lang]}
                        aria-current={active === d.id}
                        tabIndex={visible ? 0 : -1}
                        className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden border transition-[background-color,border-color,transform] duration-300 ${active === d.id ? 'bg-white/80 border-primary scale-105' : 'bg-white/30 backdrop-blur-xl border-white/70'}`}
                        data-testid={`dock-${d.id}`}
                    >
                        <img src={DOSSIER_IMAGES[d.id]} alt="" className="w-9 h-9 object-contain" />
                    </button>
                ))}
            </div>
        </nav>
    );
}
